import { useEffect, useMemo, useRef, useState } from "react"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { Each } from "../common/Each"
import useClickOutside from "../common/hooks/useClickOutside"
import styles from "./MultipleSelect.module.css"
import Checkbox from "./Checkbox"

/**
 * MultipleSelect - Un componente React per selezionare più opzioni da un elenco.
 * 
 * Props:
 * @param {Array} selected - Gli elementi inizialmente selezionati (array di oggetti con almeno un `id` e `name`).
 * @param {Array} options - L'elenco delle opzioni disponibili (array di oggetti con almeno un `id` e `name`).
 * @param {Array} customOptions - Opzioni personalizzate con callback specifici per la selezione.
 *                                Ogni elemento è un oggetto con i seguenti campi:
 *                                - `id` (string): Identificativo univoco dell'opzione personalizzata.
 *                                - `name` (string): Nome dell'opzione personalizzata.
 *                                - `selected` (boolean): Indica se l'opzione personalizzata è selezionata.
 *                                - `onSelect` (function): Callback chiamato quando l'opzione viene selezionata o deselezionata.
 *                                Questo parametro è utile per implementare comportamenti personalizzati, come la selezione rapida
 *                                di un gruppo di elementi basati su criteri specifici.
 * @param {string} placeholder - Testo da mostrare quando nessuna opzione è selezionata.
 * @param {Object} style - Stile inline per il contenitore principale.
 * @param {Function} onSelect - Callback chiamato quando cambia la selezione. Riceve l'elenco aggiornato degli elementi selezionati.
 * @param {string} theme - Tema del componente, può essere 'dark' o 'light'.
 * @param {string} allLabel - Testo da mostrare per l'opzione "Seleziona tutto". Default: 'Tutti'.
 * @param {boolean} hideArrow - Se true, nasconde l'icona della freccia. Default: false.
 * @param {boolean} disabled - Se true, disabilita il componente. Default: false.
 *
 * 
 * Comportamento:
 * - Mostra un'etichetta con il nome dell'opzione selezionata o il numero di opzioni selezionate.
 * - Supporta la selezione di tutte le opzioni tramite un'opzione "Tutti".
 * - Disabilita l'interazione se la prop `disabled` è true.
 */

const MultipleSelect = ({ selected, options, customOptions, placeholder, style, onSelect = () => { }, theme, allLabel = 'Tutti', hideArrow = false, disabled = false }) => {

    const [value, setValue] = useState(selected)
    const [opts, setOpts] = useState(options)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const [top, setTop] = useState(null)
    const [containerRadius, setContainerRadius] = useState('1rem')
    const [optionsRadius, setOptionsRadius] = useState('1rem')
    const containerRef = useRef(null)
    const ref = useRef(null)

    const handleClickOutside = () => {
        setOptionsOpen(false)
    }

    useClickOutside(containerRef, handleClickOutside)

    useEffect(() => {

        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (containerRef.current && ref.current) {
                let containerRect = containerRef.current.getBoundingClientRect()
                if (window.innerHeight - (containerRect.y + containerRect.height) < 150) {
                    setTop(-ref.current.clientHeight - 2)
                    setOptionsRadius('1rem 1rem 0 0')
                    setContainerRadius('0 0 1rem 1rem')
                }
                else {
                    setTop(containerRef.current.clientHeight)
                    setOptionsRadius('0 0 1rem 1rem')
                    setContainerRadius('1rem 1rem 0 0')
                }
            }
        });
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect(); // clean up 
    }, []);

    useEffect(() => {
        var opts = options
        setOpts(opts)
    }, [options])


    let allSelected = useMemo(() => {
        return opts.map(v => v.id).every(v => value.map(o => o.id).includes(v))
    }, [opts, value])

    const handleOnSelect = async (item) => {
        let newValue = value
        if (value.map(v => v.id).includes(item.id)) {
            newValue = value.filter(v => v.id !== item.id)
        }
        else {
            newValue = [...value, item]
        }
        setValue(newValue)
        onSelect(newValue)
    }

    useEffect(() => {
        setValue(selected)
    }, [selected])

    useEffect(() => {
        if (!containerRef?.current || !ref?.current) return;

        if (containerRef && ref && containerRef.current && ref.current) {
            setTimeout(() => {
                if (containerRef && ref && containerRef.current && ref.current) {
                    let containerRect = containerRef.current.getBoundingClientRect()
                    if (window.innerHeight - (containerRect.y + containerRect.height) < 150) {
                        setTop(-ref.current.clientHeight - 2)
                        setOptionsRadius('1rem 1rem 0 0')
                        setContainerRadius('0 0 1rem 1rem')
                    }
                    else {
                        setTop(containerRef.current.clientHeight)
                        setOptionsRadius('0 0 1rem 1rem')
                        setContainerRadius('1rem 1rem 0 0')
                    }
                }
            }, 200)
        }
    }, [containerRef.current, ref.current, options])

    const selectAll = () => {
        let newValue = []
        if (!allSelected) {
            newValue = opts
            setValue(newValue)
        }
        else {
            newValue = []
            setValue(newValue)
        }
        onSelect(newValue)
    }


    return (
        <div
            style={{ ...style, borderRadius: optionsOpen && !disabled ? containerRadius : '1rem' }}
            className={`${styles.container} ${optionsOpen && !disabled ? styles.open : ''} ${theme === 'dark' ? styles.dark : ''} ${disabled ? styles.disabled : ''}`}
            ref={containerRef}
            onClick={() => {
                if (options.length > 0 && !disabled) {
                    setOptionsOpen(!optionsOpen)
                }
            }} >
            <div className={styles.selectedLabel}>
                {value && value.length === 1 &&
                    <>
                        {value[0].name}
                    </>
                }
                {value && value.length > 1 &&
                    <>{value.length === opts.length ? (allLabel ? allLabel : 'Tutti') : value.length + ' selezionati'}</>
                }
                {(!value || value.length === 0) &&
                    <>
                        {placeholder}
                    </>
                }
            </div>

            <div className={`${styles.options} ${(opts && opts.length > 0 && optionsOpen) ? styles.open : ''}`} ref={ref} style={{ top: `${top}px`, borderRadius: optionsRadius }} onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
            }}>
                <>
                    <div className={`${styles.option} ${allSelected ? styles.selected : ''}`} onClick={() => {
                        selectAll()
                    }}>
                        <Checkbox size="1.5rem" value={allSelected} style={{ borderRadius: '4px' }} /> Tutti
                    </div>
                    <Each of={customOptions} render={(option) => {
                        return (
                            <div className={`${styles.option} ${value?.id === option.id ? styles.selected : ''}`} onClick={() => {
                                option.onSelect(!option.selected)
                            }}>
                                <Checkbox size="1.5rem" value={option.selected} /> {option.name}
                            </div>
                        )
                    }} />
                    <Each of={opts} render={(item) => {
                        return (
                            <div className={`${styles.option} ${value?.id === item.id ? styles.selected : ''}`} onClick={() => {
                                handleOnSelect(item)
                            }}>
                                <Checkbox size="1.5rem" value={value.map(v => v.id).includes(item.id)} style={{ borderRadius: '4px' }} /> {item.name}
                            </div>
                        )
                    }} />
                </>
            </div>

            {!hideArrow &&
                <ChevronIcon className={`${styles.chevron} ${optionsOpen ? styles.open : ''}`} />
            }
        </div>
    )

}

export default MultipleSelect